import { Tabs, Breadcrumb, Skeleton } from 'antd';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import SidebarLayout from 'components/Layout/Layout';
import { API_URL } from 'env';
import type { User } from 'types/user';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import UserHeaderCard from './components/UserHeaderCard';
import InfoTab from './components/InfoTab';
import LoginHistoryTab from './components/LoginHistoryTab';

interface RawUserDetail {
    id: string;
    first_name?: string;
    last_name?: string;
    full_name_display?: string;
    fullname?: string;
    email: string;
    phone?: string;
    job_title?: string;
    kyc_verified?: boolean;
    country?: string;
    city?: string;
    address?: string;
    postal_code?: string;
    date_of_birth?: string;
    gender?: string;
    avatar_url?: string;
    is_active?: number;
    account_type?: 'std' | 'vip' | 'pre';
    last_login?: string;
    created_at?: string;
    updated_at?: string;
}

const UserMainPage = () => {
    const { userID } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState<User | undefined>(undefined);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userID) return;
        setLoading(true);
        fetch(`${API_URL}api/v1/be/user/${userID}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
        })
            .then(r => r.json())
            .then(res => {
                if (!res.data) return;
                const d: RawUserDetail = res.data;
                setUser({
                    id: d.id,
                    name: d.full_name_display || d.fullname || d.email || '-',
                    firstName: d.first_name || '',
                    lastName: d.last_name || '',
                    email: d.email,
                    phonenumber: d.phone || '',
                    jobTitle: d.job_title || '',
                    KYC: d.kyc_verified,
                    country: d.country || '',
                    city: d.city || '',
                    address: d.address || '',
                    postalCode: d.postal_code || '',
                    dateOfBirth: d.date_of_birth || '',
                    gender: d.gender || '',
                    avatar_url: d.avatar_url || '',
                    isActive: d.is_active === 1,
                    status: d.is_active === 1 ? 'Active' : 'Inactive',
                    accountType: d.account_type,
                    lastLogin: d.last_login || '',
                    createAt: d.created_at || '',
                    updateAt: d.updated_at || '',
                    balance: 0,
                });
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [userID]);

    return (
        <SidebarLayout>
            <div className="space-y-4">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigate('/users')}
                        className="flex items-center justify-center w-7 h-7 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors cursor-pointer"
                    >
                        <ArrowLeftIcon size={16} className="text-white" />
                    </button>
                    <Breadcrumb
                        separator=">"
                        items={[
                            { title: <span className="cursor-pointer" onClick={() => navigate('/users')}>Người dùng</span> },
                            { title: loading ? '...' : (user?.name ?? '') },
                        ]}
                    />
                </div>

                {loading ? (
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 space-y-4">
                        <Skeleton avatar active paragraph={{ rows: 2 }} />
                        <Skeleton active paragraph={{ rows: 1 }} />
                    </div>
                ) : user ? (
                    <>
                        <UserHeaderCard user={user} />
                        <div className="bg-white dark:bg-gray-800 rounded-xl">
                            <Tabs
                                tabBarStyle={{ paddingLeft: 24, paddingRight: 24 }}
                                items={[
                                    { key: 'info', label: 'Thông tin', children: <InfoTab user={user} /> },
                                    { key: 'login', label: 'Lịch sử đăng nhập', children: <LoginHistoryTab /> },
                                ]}
                            />
                        </div>
                    </>
                ) : (
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-12 text-center text-gray-400">
                        Không tìm thấy người dùng
                    </div>
                )}
            </div>
        </SidebarLayout>
    );
};

export default UserMainPage;
